export const BREADCRUMB_BASE_URL = "https://bharatbulksms.com";

export interface BreadcrumbSchemaItem {
  name: string;
  item: string;
}

const pageLabels: Record<string, string> = {
  "/about": "About Us",
  "/services": "Services",
  "/pricing": "Pricing",
  "/contact": "Contact",
  "/faq": "FAQ",
  "/blog": "Blog",
  "/industries": "Industries",
  "/testimonials": "How We Work",
  "/case-studies": "Messaging Use Cases",
  "/resources": "Resources",
  "/tools": "Tools",
  "/api-docs": "SMS API Documentation",
  "/write-for-us": "Write for Us",
  "/sms-marketing-statistics-india": "SMS Marketing Statistics India 2026",
  "/dlt-guide": "DLT Registration Guide",
  "/dlt-compliance": "DLT Compliance Guide",
  "/bulk-sms-india": "Bulk SMS India",
  "/whatsapp-api-marketing": "WhatsApp API Marketing",
  "/privacy-policy": "Privacy Policy",
  "/terms-conditions": "Terms and Conditions",
  "/refund-policy": "Refund Policy",
  "/cookie-policy": "Cookie Policy",
  "/disclaimer": "Disclaimer",
  "/comparison/msg91": "Provider Evaluation Guide",
  "/comparison/textlocal": "Provider Evaluation Guide",
  "/tools/dlt-validator": "DLT Template Validator",
  "/tools/template-generator": "AI Template Generator",
  "/tools/sms-character-counter": "SMS Character Counter",
  "/tools/whatsapp-number-scrubber": "WhatsApp Number Scrubber",
  "/tools/seo-checker": "SEO Checker",
  "/industries/banking-finance": "Banking and Finance",
  "/industries/e-commerce": "E-Commerce",
  "/industries/healthcare": "Healthcare",
  "/industries/education": "Education",
  "/industries/real-estate": "Real Estate",
  "/industries/retail": "Retail",
  "/industries/logistics": "Logistics",
  "/industries/restaurants-food": "Restaurants and Food",
  "/industries/travel-tourism": "Travel and Tourism",
  "/industries/manufacturing": "Manufacturing",
  "/industries/technology-saas": "Technology and SaaS",
  "/industries/automobiles": "Automobiles",
  "/blog/dlt-registration-guide": "DLT Registration Guide",
  "/blog/dlt-template-approval-process": "DLT Template Approval",
  "/blog/promotional-vs-transactional-sms-india": "Promotional vs Transactional SMS",
  "/blog/how-to-send-bulk-sms-india": "How to Send Bulk SMS in India",
  "/blog/whatsapp-business-api-vs-bulk-sms": "WhatsApp API vs Bulk SMS",
  "/blog/msg91-alternative": "MSG91 Alternative Guide",
  "/blog/best-bulk-sms-api-india": "Bulk SMS API Guide",
  "/blog/how-to-send-otp-sms-india": "How to Send OTP SMS",
  "/blog/bulk-sms-for-ecommerce-india": "Bulk SMS for E-Commerce",
  "/blog/whatsapp-api-pricing-india": "WhatsApp API Pricing Guide",
  "/blog/sms-api-integration-india": "SMS API Integration Guide",
  "/blog/bulk-sms-for-real-estate-india": "Bulk SMS for Real Estate",
  "/blog/bulk-sms-for-healthcare-india": "Bulk SMS for Healthcare",
  "/blog/bulk-sms-for-education-india": "Bulk SMS for Education",
  "/blog/sms-marketing-roi-india": "SMS Marketing ROI",
  "/case-studies/quickcart-ecommerce-sms": "E-Commerce Order Updates",
  "/case-studies/medlife-hospitals-sms": "Healthcare Appointment Reminders",
  "/case-studies/swiftlogistics-sms": "Shipment Tracking Messages",
};

const serviceLabels: Record<string, string> = {
  "/bulk-sms": "Bulk SMS",
  "/transactional-sms": "Transactional SMS",
  "/promotional-sms": "Promotional SMS",
  "/otp-sms": "OTP SMS",
  "/whatsapp-business-api": "WhatsApp Business API",
  "/voice-call": "Voice Call",
  "/google-ads-services": "Google Ads",
  "/seo-services": "SEO Services",
  "/rcs-messaging": "RCS Messaging",
  "/email-marketing": "Email Marketing",
  "/social-media-marketing": "Social Media Marketing",
  "/website-design": "Website Design",
  "/meta-ads": "Meta Ads",
  "/lead-generation": "Lead Generation",
  "/content-marketing": "Content Marketing",
  "/local-seo": "Local SEO",
  "/dlt-registration": "DLT Registration",
  "/social-media-monetization": "Social Media Monetization",
  "/services/toll-free-number": "Toll-Free Number",
  "/services/ivr-solutions": "IVR Solutions",
  "/services/missed-call-services": "Missed Call Services",
  "/services/youtube-ads": "YouTube Ads",
  "/reseller-program": "Reseller Program",
  "/international-bulk-sms": "International Bulk SMS",
};

const retiredRoutePatterns = [
  /^\/bulk-sms-service-/,
  /^\/blog\/bulk-sms-service-provider-/,
];

function normalizePathname(pathname: string): string {
  const normalized = pathname.split("?")[0].replace(/\/$/, "");
  return normalized || "/";
}

function toReadableLabel(pathname: string): string {
  const segment = pathname.split("/").filter(Boolean).at(-1) ?? "";
  return segment
    .split("-")
    .filter(Boolean)
    .map((word) => word.toUpperCase() === "SMS" || word.toUpperCase() === "API" || word.toUpperCase() === "SEO" || word.toUpperCase() === "DLT" || word.toUpperCase() === "IVR" || word.toUpperCase() === "RCS"
      ? word.toUpperCase()
      : `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function item(name: string, pathname: string): BreadcrumbSchemaItem {
  return { name, item: `${BREADCRUMB_BASE_URL}${pathname === "/" ? "/" : pathname}` };
}

/**
 * Returns only canonical, navigable breadcrumb paths. Retired city routes are
 * deliberately excluded because their permanent redirects should not emit a
 * second page's structured data.
 */
export function getBreadcrumbItems(pathname: string): BreadcrumbSchemaItem[] | null {
  const path = normalizePathname(pathname);
  if (path === "/" || retiredRoutePatterns.some((pattern) => pattern.test(path))) return null;

  const label = serviceLabels[path] ?? pageLabels[path];
  if (!label) return null;

  const items: BreadcrumbSchemaItem[] = [item("Home", "/")];

  if (serviceLabels[path]) {
    items.push(item("Services", "/services"));
  } else if (path.startsWith("/industries/")) {
    items.push(item("Industries", "/industries"));
  } else if (path.startsWith("/blog/")) {
    items.push(item("Blog", "/blog"));
  } else if (path.startsWith("/tools/")) {
    items.push(item("Tools", "/tools"));
  } else if (path.startsWith("/comparison/")) {
    items.push(item("Resources", "/resources"));
  } else if (path.startsWith("/case-studies/")) {
    items.push(item("Messaging Use Cases", "/case-studies"));
  } else if (path === "/sms-marketing-statistics-india") {
    items.push(item("Resources", "/resources"));
  }

  items.push(item(label, path));
  return items;
}

export function getBreadcrumbSchema(pathname: string): object | null {
  const items = getBreadcrumbItems(pathname);
  if (!items) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((breadcrumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: breadcrumb.name,
      item: breadcrumb.item,
    })),
  };
}
